import { api, endpoints } from "api/api";
import { AxiosPromise } from "axios";
import { BaseApiPaging } from "../types";
import { UserApi } from "../users/usersApi";
import { InvoiceType, RefundStatus } from "consts";

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string,
  orderBy: string,
): AxiosPromise<BaseApiPaging<PaymentApi>> => {
  return api.get<BaseApiPaging<PaymentApi>>(endpoints.PAYMENTS.GET(skip, take, searchPhrase, filter, orderBy));
};

const fetchDelayed = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string,
  orderBy: string,
): AxiosPromise<BaseApiPaging<DelayedPaymentApi>> => {
  return api.get<BaseApiPaging<DelayedPaymentApi>>(
    endpoints.PAYMENTS.DELAYED(skip, take, searchPhrase, filter, orderBy),
  );
};

const refund = (paymentId: number, body: { amount: number; note?: string }): AxiosPromise<RefundResultApi> => {
  return api.put<RefundResultApi>(endpoints.PAYMENTS.REFUND(paymentId), body);
};

const retryRefund = (refundId: number): AxiosPromise<RefundResultApi> => {
  return api.post<RefundResultApi>(endpoints.PAYMENTS.RETRY_REFUND(refundId));
};

const regenerateInvoice = (paymentId: number): AxiosPromise<void> => {
  return api.put<void>(endpoints.PAYMENTS.REGENERATE_INVOICE(paymentId));
};

const updateInvoiceConfiguration = (
  paymentId: number,
  body: InvoiceConfiguration,
  courseId?: number,
  generateInvoice = true,
): AxiosPromise<void> => {
  const base = endpoints.PAYMENTS.UPDATE_INVOICE_CONFIGURATION(paymentId, courseId);
  const url = generateInvoice ? base : `${base}${courseId ? "&" : "?"}generateInvoice=false`;
  return api.put<void>(url, body);
};
const createInvoiceConfiguration = (paymentId: number, courseId?: number): AxiosPromise<void> => {
  return api.post<void>(endpoints.PAYMENTS.UPDATE_INVOICE_CONFIGURATION(paymentId, courseId));
};

const fetchInvoiceConfiguration = (paymentId: number, courseId?: number): AxiosPromise<InvoiceConfiguration> => {
  return api.get<InvoiceConfiguration>(endpoints.PAYMENTS.FETCH_INVOICE_CONFIGURATION(paymentId, courseId));
};

const fetchKsefXml = (paymentId: number, courseId?: number): AxiosPromise<{ xml: string | null }> => {
  return api.get<{ xml: string | null }>(endpoints.PAYMENTS.FETCH_KSEF_XML(paymentId, courseId));
};

const generateKsefXml = (paymentId: number, courseId?: number): AxiosPromise<{ xml: string | null }> => {
  return api.post<{ xml: string | null }>(endpoints.PAYMENTS.GENERATE_KSEF_XML(paymentId, courseId));
};

const updateKsefXml = (paymentId: number, xml: string | null, courseId?: number): AxiosPromise<void> => {
  return api.put<void>(endpoints.PAYMENTS.UPDATE_KSEF_XML(paymentId, courseId), { xml });
};

const submitToKsef = (paymentId: number, courseId?: number): AxiosPromise<void> => {
  return api.post<void>(endpoints.PAYMENTS.SUBMIT_TO_KSEF(paymentId, courseId));
};

const downloadKsefInvoicePdf = (paymentId: number, courseId?: number): Promise<Blob> => {
  return api
    .get<Blob>(endpoints.PAYMENTS.FETCH_KSEF_INVOICE_PDF(paymentId, courseId), { responseType: "blob" })
    .then((r) => r.data);
};

export const paymentsApi = {
  fetch,
  fetchDelayed,
  refund,
  retryRefund,
  regenerateInvoice,
  updateInvoiceConfiguration,
  fetchInvoiceConfiguration,
  createInvoiceConfiguration,
  fetchKsefXml,
  generateKsefXml,
  updateKsefXml,
  submitToKsef,
  downloadKsefInvoicePdf,
};

export interface PaymentInvoiceApi {
  id: number;
  month: number;
  name: string;
  number: number;
  type: InvoiceType;
  year: number;
  kSeFSubmitted: boolean;
  kSeFNumber: string | null;
  kSeFAcceptedAt: string | null;
}
export type RefundResultApi = { refundId: number; status: RefundStatus; error: string | null };

export interface PaymentApi {
  amount: string;
  comment?: string | null;
  courseId?: number; // Nullable for multi-course payments
  courseName?: string; // Nullable for multi-course payments
  courses?: {
    // Nullable for single-course payments
    allocatedAmount: number;
    courseId: number;
    courseName: string;
    hasInvoice: boolean;
    invoice?: {
      id: number;
      month: number;
      name: string;
      number: number;
      type: InvoiceType;
      year: number;
      kSeFSubmitted: boolean;
      kSeFNumber: string | null;
      kSeFAcceptedAt: string | null;
    };
  }[];
  createdDate: Date;
  gatewayID: string;
  hasInvoice: boolean; // For single-course payments
  id: number;
  invoice?: PaymentInvoiceApi; // For single-course payments
  isMultiCourse: boolean;
  orderID: string;
  outsideSystem?: boolean;
  paymentLink: string;
  registrationId?: number; // Nullable for multi-course payments
  remoteId: string;
  refundOfId: number | null;
  refundStatus: RefundStatus | null;
  refundError: string | null;
  refunds: { id: number; amount: string; status: RefundStatus | null }[];
  status: number;
  userExamId: number | null;
  userExamName: string | null;
  user: UserApi;
}

export interface GenerateCustomInvoiceBody {
  invoiceNumber: string;
  saleDate: Date;
  paymentMethod: string;
  orderNumber: string;
  rows: InvoiceDocumentRow[];
  buyer: RegistrationBuyerDto;
  recipient?: RegistrationRecipientDto;
  previousAdvancedPayments?: PreviousAdvancedPayment[];
  ratesSettlement?: RatesSettlement[];
}

export interface InvoiceDocumentRow {
  lp: string;
  nazwa: string;
  jedn: string;
  ilosc: string;
  cenaNetto: string;
  stawka: string;
  wartoscNetto: string;
  wartoscBrutton: string;
}

export interface RegistrationBuyerDto {
  buyer: string | null;
  buyerAddress: string | null;
  buyerNip: string | null;
}

export interface RegistrationRecipientDto {
  recipient: string | null;
  recipientAddress: string | null;
  recipientNip: string | null;
}

export interface InvoiceConfiguration {
  type: InvoiceType;
  number: number;
  year: number;
  month: number;
  header: InvoiceHeader;
  seller: InvoiceSeller;
  buyer: RegistrationBuyerDto;
  recipient: RegistrationRecipientDto | null;
  rows: InvoiceDocumentRow[];
  orderSummaryRows: OrderSummaryRow[];
  invoiceSums: InvoiceSums;
  footer: InvoiceFooter;
  previousAdvancedPayments: PreviousAdvancedPayment[];
  ratesSettlement: RatesSettlement[] | null;
}

export interface InvoiceHeader {
  topLeftLogoPath: string;
  topRightTableHeader: string;
  creationDate: Date;
  saleDate: Date;
  paymentMethod: string;
  orderNumber: string;
}

export interface InvoiceSeller {
  name: string;
  address: string;
  nip: string;
  bankAccount: string;
}

export interface OrderSummaryRow {
  vatRate: string;
  netValue: number;
  vatValue: number;
  grossValue: number;
}

export interface InvoiceSums {
  paid: number;
  toPay: number;
  total: number;
}

export interface InvoiceFooter {
  invoiceOwner: string;
  phone: string;
  email: string;
}

export interface PreviousAdvancedPayment {
  invoiceNumber: string;
  amount: number;
  creationDate: Date;
}

export interface RatesSettlement {
  vatRate: string;
  netValue: number;
  vatValue: number;
  grossValue: number;
}

export interface DelayedPaymentApi {
  // Tranche Information
  courseTrancheId: number;
  trancheName: string | null;
  trancheAmount: number;
  discountedAmount: number;
  dueDate: Date;
  daysOverdue: number;
  status: number;

  // Registration Information
  registrationId: number;
  registrationCreatedDate: Date;
  isInvoice: boolean | null;
  buyer: string | null;
  buyerNIP: string | null;

  // User Information
  userId: number;
  userFullName: string;
  userEmail: string;
  userPhone: string | null;
  userPesel: string | null;

  // Course Information
  courseId: number;
  courseName: string;
  courseEdition: string | null;
  courseStartDate: Date | null;

  // Reminder Information
  lastReminderSentTime: Date | null;
  lastReminderTemplateType: number | null;
}
